import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaQuoteLeft } from 'react-icons/fa';
import { useFetchWrapper } from '../../../_helpers/fetchWrapper.js';
import config from '../../../config.js';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { richMessage } from '../../../_helpers/richMessage.jsx';
import navStyles from '../../../styles/partial/navigation/PerformerNavSongList.module.css';

const PerformerNavSongList = ({ onImportSonglist }) => {
  const { bandid } = useParams();
  const t = useT();
  const fetchWrapperRef = useRef(useFetchWrapper());

  const [bandMessage, setBandMessage] = useState('');
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [editMessageValue, setEditMessageValue] = useState('');
  const [savingMessage, setSavingMessage] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [publicSongs, setPublicSongs] = useState([]);
  const [bandName, setBandName] = useState('');

  const maxChars = 1000;

  const fetchBandMessage = useCallback(async () => {
    try {
      const data = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandid}/message/songlist`);
      setBandMessage(data?.message || '');
    } catch {
      setBandMessage('');
    }
  }, [bandid]);

  const fetchPublicData = useCallback(async () => {
    try {
      const [songsRes, bandRes] = await Promise.all([
        fetchWrapperRef.current.get(`${config.apiBaseUrl}/public/bands/${bandid}/songs`).catch(() => []),
        fetchWrapperRef.current.get(`${config.apiBaseUrl}/public/bands/${bandid}`).catch(() => null),
      ]);
      const songs = Array.isArray(songsRes) ? songsRes : (songsRes?.songs || []);
      // The public endpoint lists only public songs, and carries no `active`
      // flag: filtering on it showed "0 public" for every band (ENG-528).
      // A song explicitly marked inactive is still left out.
      setPublicSongs(songs.filter(s => s.active !== false));
      if (bandRes?.name) setBandName(bandRes.name);
    } catch {
      setPublicSongs([]);
    }
  }, [bandid]);

  useEffect(() => {
    fetchBandMessage();
    fetchPublicData();
  }, [fetchBandMessage, fetchPublicData]);

  const handleStartEditMessage = () => {
    setEditMessageValue(bandMessage);
    setIsEditingMessage(true);
  };

  const handleSaveMessage = async () => {
    setSavingMessage(true);
    setSaveError(null);
    try {
      await fetchWrapperRef.current.put(`${config.apiBaseUrl}/bands/${bandid}/message/songlist`, { message: editMessageValue });
      setBandMessage(editMessageValue);
      setIsEditingMessage(false);
    } catch (err) {
      console.error('Error saving band message:', err);
      setSaveError(t('common.errors.saveFailed'));
    } finally {
      setSavingMessage(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingMessage(false);
    setEditMessageValue('');
    setSaveError(null);
  };

  const bandInitials = bandName
    ? bandName.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : 'B';

  const PREVIEW_COUNT = 9;
  const previewSongs = publicSongs.slice(0, PREVIEW_COUNT);
  const needsOverlay = publicSongs.length > PREVIEW_COUNT;
  const displaySongs = needsOverlay ? previewSongs.slice(0, PREVIEW_COUNT - 1) : previewSongs;
  const extraCount = needsOverlay ? publicSongs.length - (PREVIEW_COUNT - 1) : 0;

  return (
    <div className={navStyles.navWrap}>
      <a
        href="#import-songlist"
        className={navStyles.btnSecondary}
        data-testid="songlist-import"
        onClick={(e) => { e.preventDefault(); if (onImportSonglist) onImportSonglist(); }}
      >
        {t('songList.nav.importSonglist')}
      </a>
      <Link to={`/performers/${bandid}/edit-tags`} className={navStyles.btnSecondary}>
        {t('songList.nav.editTags')}
      </Link>

      {/* Listener Message Card */}
      <div className={navStyles.listenerCard}>
        <div className={navStyles.cardHeader}>
          <span className={navStyles.sectionLabel}>{t('songList.listenerMessage.title')}</span>
          <div className={navStyles.cardHeaderSpacer} />
          {!isEditingMessage && (
            <button
              type="button"
              className={navStyles.editLink}
              onClick={handleStartEditMessage}
            >
              {t('common.actions.edit')}
            </button>
          )}
        </div>

        <p className={navStyles.descriptionText}>
          {t('songList.listenerMessage.description')}
        </p>

        {isEditingMessage ? (
          <div className={navStyles.messageEditWrap}>
            <textarea
              className={navStyles.messageTextarea}
              value={editMessageValue}
              onChange={(e) => setEditMessageValue(e.target.value)}
              placeholder={t('songList.listenerMessage.placeholder')}
              maxLength={maxChars}
            />
            {saveError && (
              <p className={navStyles.saveError}>{saveError}</p>
            )}
            <div className={navStyles.messageEditControls}>
              <span className={navStyles.charCount}>{t('songList.listenerMessage.charCount', { current: editMessageValue.length, max: maxChars })}</span>
              <button
                type="button"
                className={navStyles.btnSave}
                onClick={handleSaveMessage}
                disabled={savingMessage}
              >
                {savingMessage ? t('common.actions.savingEllipsis') : t('common.actions.save')}
              </button>
              <button
                type="button"
                className={navStyles.btnCancel}
                onClick={handleCancelEdit}
              >
                {t('common.actions.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className={navStyles.notePlate}>
            <FaQuoteLeft className={navStyles.quoteIcon} aria-hidden="true" />
            <p className={navStyles.messageText}>
              {bandMessage || (
                <span className={navStyles.messagePlaceholder}>
                  {t('songList.listenerMessage.empty')}
                </span>
              )}
            </p>
            {bandName && (
              <div className={navStyles.messageAttribution}>
                <span className={navStyles.bandInitials}>{bandInitials}</span>
                <span className={navStyles.bandNameLabel}>{bandName}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Public Songlist Card */}
      <div className={navStyles.publicCard}>
        <div className={navStyles.cardHeaderRow}>
          <span className={navStyles.sectionLabel}>{t('songList.publicCard.title')}</span>
          <div className={navStyles.cardHeaderSpacer} />
          <span className={navStyles.publicCountBadge}>{t('songList.publicCard.publicCount', { count: publicSongs.length })}</span>
        </div>

        {displaySongs.length > 0 && (
          <div className={navStyles.artworkGrid}>
            {displaySongs.map((song, i) => {
              const artworkUrl = song.artwork_url
                ? `${config.mediaBaseUrl}/${song.artwork_url}`
                : null;
              return (
                <div key={song.id ?? i} className={navStyles.artworkCell}>
                  {artworkUrl ? (
                    <img src={artworkUrl} alt={song.title || ''} className={navStyles.artworkImg} />
                  ) : (
                    <div className={navStyles.artworkFallback}>
                      {(song.title || '?')[0].toUpperCase()}
                    </div>
                  )}
                </div>
              );
            })}
            {extraCount > 0 && (
              <div className={`${navStyles.artworkCell} ${navStyles.artworkOverlay}`}>
                {richMessage(
                  t('songList.publicCard.extraCount', { count: extraCount }),
                  { extra: <span>{t('songList.publicCard.extraBadge', { count: extraCount })}</span> },
                  { text: (chunk) => <span className={navStyles.artworkOverlayMore}>{chunk}</span> }
                )}
              </div>
            )}
          </div>
        )}

        <div className={navStyles.cardHairline} />
        <Link
          to={`/public/bands/${bandid}/songlist`}
          className={navStyles.viewPublicLink}
        >
          {t('songList.publicCard.viewPublic')}
        </Link>
      </div>
    </div>
  );
};

export default PerformerNavSongList;
